import React, { useEffect, useState } from 'react';
import { SaveIcon, UsersIcon, BriefcaseIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
import { CountrySelector } from '../settings/CountrySelector';
export function BankAccountForm({
  bankAccount,
  onSave,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    id: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    currency: 'USD',
    branchName: '',
    accountType: 'Checking',
    country: '',
    isPrimary: false
  });
  const accountTypes = ['Checking', 'Savings', 'Business', 'Current', 'Investment', 'Other'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];
  useEffect(() => {
    if (bankAccount) {
      setFormData({
        ...bankAccount
      });
    } else {
      setFormData({
        id: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        routingNumber: '',
        swiftCode: '',
        iban: '',
        currency: 'USD',
        branchName: '',
        accountType: 'Checking',
        country: '',
        isPrimary: false
      });
    }
  }, [bankAccount]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleCountryChange = countryCode => {
    setFormData({
      ...formData,
      country: countryCode
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const getAssociationIcon = type => {
    switch (type) {
      case 'Vendor':
        return <BriefcaseIcon className="h-4 w-4 mr-2" data-id="element-6795" />;
      case 'Entity':
        return <BuildingIcon className="h-4 w-4 mr-2" data-id="element-6796" />;
      case 'Site':
        return <MapPinIcon className="h-4 w-4 mr-2" data-id="element-6797" />;
      case 'Org Unit':
        return <UsersIcon className="h-4 w-4 mr-2" data-id="element-6798" />;
      default:
        return <UsersIcon className="h-4 w-4 mr-2" data-id="element-6799" />;
    }
  };
  const getAssociationColor = type => {
    switch (type) {
      case 'Vendor':
        return 'bg-blue-100 text-blue-800';
      case 'Entity':
        return 'bg-purple-100 text-purple-800';
      case 'Site':
        return 'bg-green-100 text-green-800';
      case 'Org Unit':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="bg-white shadow sm:rounded-lg" data-id="element-6800">
      <div className="px-4 py-5 sm:p-6" data-id="element-6801">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6802">
          {bankAccount ? 'Edit Bank Account' : 'Add Bank Account'}
        </h3>

        {/* Show associations if editing existing account */}
        {bankAccount && bankAccount.associations && bankAccount.associations.length > 0 && <div className="mt-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-6803">
              <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-6804">
                Used In ({bankAccount.associations.length})
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-6805">
                {bankAccount.associations.map((assoc, idx) => <span key={idx} className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getAssociationColor(assoc.type)}`} data-id="element-6806">
                    {getAssociationIcon(assoc.type)}
                    {assoc.name}
                  </span>)}
              </div>
              <p className="mt-2 text-xs text-gray-500" data-id="element-6807">
                This bank account is currently being used by the above entities.
                Changes will affect all associated records.
              </p>
            </div>}

        <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-6808">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6809">
            <div className="sm:col-span-3" data-id="element-6810">
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700" data-id="element-6811">
                Bank Name
              </label>
              <div className="mt-1" data-id="element-6812">
                <input type="text" name="bankName" id="bankName" value={formData.bankName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-6813" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6814">
              <label htmlFor="accountName" className="block text-sm font-medium text-gray-700" data-id="element-6815">
                Account Name
              </label>
              <div className="mt-1" data-id="element-6816">
                <input type="text" name="accountName" id="accountName" value={formData.accountName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-6817" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6818">
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700" data-id="element-6819">
                Account Number
              </label>
              <div className="mt-1" data-id="element-6820">
                <input type="text" name="accountNumber" id="accountNumber" value={formData.accountNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-6821" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6822">
              <label htmlFor="accountType" className="block text-sm font-medium text-gray-700" data-id="element-6823">
                Account Type
              </label>
              <div className="mt-1" data-id="element-6824">
                <select id="accountType" name="accountType" value={formData.accountType} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6825">
                  {accountTypes.map(type => <option key={type} value={type} data-id="element-6826">
                      {type}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6827">
              <label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700" data-id="element-6828">
                Routing Number / Sort Code
              </label>
              <div className="mt-1" data-id="element-6829">
                <input type="text" name="routingNumber" id="routingNumber" value={formData.routingNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6830" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6831">
              <label htmlFor="swiftCode" className="block text-sm font-medium text-gray-700" data-id="element-6832">
                SWIFT / BIC Code
              </label>
              <div className="mt-1" data-id="element-6833">
                <input type="text" name="swiftCode" id="swiftCode" value={formData.swiftCode} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6834" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6835">
              <label htmlFor="iban" className="block text-sm font-medium text-gray-700" data-id="element-6836">
                IBAN
              </label>
              <div className="mt-1" data-id="element-6837">
                <input type="text" name="iban" id="iban" value={formData.iban} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6838" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6839">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-6840">
                Currency
              </label>
              <div className="mt-1" data-id="element-6841">
                <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6842">
                  {currencies.map(currency => <option key={currency} value={currency} data-id="element-6843">
                      {currency}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6844">
              <label htmlFor="branchName" className="block text-sm font-medium text-gray-700" data-id="element-6845">
                Branch Name
              </label>
              <div className="mt-1" data-id="element-6846">
                <input type="text" name="branchName" id="branchName" value={formData.branchName} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6847" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-6848">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-6849">
                Bank Country
              </label>
              <div className="mt-1" data-id="element-6850">
                <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-6851" />
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-6852">
              <div className="flex items-center h-full" data-id="element-6853">
                <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6854" />
                <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-6855">
                  Is Primary Account
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3" data-id="element-6856">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6857">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6858">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-6859" />
              Save Bank Account
            </button>
          </div>
        </form>
      </div>
    </div>;
}